import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "../../contexts/useAuth";
import { AppRoutes } from "../../types/enums/appRoutes";

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Gates its children on authentication. Unauthenticated users are redirected to
 * the login page. The originating location is stashed in `location.state.from`
 * for potential future return-after-login; the MSAL redirect flow currently
 * lands users back at the root. Authentication only — no role checks.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="auth-guard__loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={AppRoutes.login} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export default AuthGuard;
